import DOMPurify from "dompurify"
import { css, html, LitElement } from "lit"
import MarkdownIt from "markdown-it"
import githubAlerts from "markdown-it-github-alerts"
import "../composants/PlanningHebdomadaire.js"
import { palettePlanning } from "../composants/stylePlanning.js"
import { parserPlanningHebdomadaire } from "../transverse/parserPlanning.js"

const mdParser = new MarkdownIt({
  html: true,
  linkify: true,
}).use(githubAlerts)

const LIENS_EN_SAVOIR_PLUS = [
  { libelle: "Comment ça marche", href: "/comment-ca-marche" },
  { libelle: "FAQ", href: "/faq" },
  { libelle: "Contact", href: "/contact" },
  { libelle: "Blog", href: "/blog" },
  { libelle: "Actualités", href: "/news" },
]

export default class PageAccueil extends LitElement {
  static properties = {
    planning: { type: Object },
    fallbackHtml: { type: String },
    texteComplementaireHtml: { type: String },
  }

  static styles = [
    palettePlanning,
    css`
      .intro {
        font-size: 1.6rem;
        line-height: 1.5;
        color: var(--planning-texte-doux);
        margin: 0 0 2rem;
        max-width: 42ch;
      }

      .separateur {
        height: 1px;
        background-color: var(--planning-bordure);
        border: none;
        margin: 0 0 2.4rem;
      }

      .en-savoir-plus {
        margin-top: 3rem;
        padding-top: 2rem;
        border-top: 1px solid var(--planning-bordure);
        text-align: center;
      }

      .en-savoir-plus .titre {
        text-transform: uppercase;
        letter-spacing: 0.1em;
        font-size: 1.2rem;
        color: var(--planning-texte-doux);
        margin: 0 0 1.2rem;
      }

      .liste-liens {
        display: flex;
        flex-wrap: wrap;
        justify-content: center;
        gap: 0.8rem;
      }

      .liste-liens a {
        font-size: 1.3rem;
        color: var(--planning-texte-doux);
        background-color: var(--planning-surface);
        border: 1px solid var(--planning-bordure);
        padding: 0.7rem 1.2rem;
        border-radius: 5rem;
        text-decoration: none;
      }

      .liste-liens a:hover,
      .liste-liens a:focus-visible {
        color: var(--couleur-texte);
        border-color: var(--planning-texte-doux);
      }
    `,
  ]

  // Le gist ne change qu'une fois par semaine : inutile de le recharger plus souvent
  // qu'une fois toutes les 5 min quand l'onglet repasse au premier plan.
  static #DELAI_RECHARGE_MS = 5 * 60 * 1000

  #dernierChargement = 0

  constructor() {
    super()
    this.planning = null
    this.fallbackHtml = ""
    this.texteComplementaireHtml = ""
  }

  #rechargerSiVisible = () => {
    if (document.visibilityState !== "visible") return
    if (Date.now() - this.#dernierChargement < PageAccueil.#DELAI_RECHARGE_MS) return
    this.chargerPlanning()
  }

  connectedCallback() {
    super.connectedCallback()
    document.addEventListener("visibilitychange", this.#rechargerSiVisible)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener("visibilitychange", this.#rechargerSiVisible)
  }

  firstUpdated() {
    this.chargerPlanning()
  }

  async chargerPlanning() {
    this.#dernierChargement = Date.now()
    try {
      // `no-store` : sans ça le cache HTTP du navigateur peut resservir une version
      // périmée du gist (l'API GitHub répond avec `max-age=60`).
      const response = await fetch(import.meta.env.VITE_GIST_URL, { cache: "no-store" })
      const data = await response.json()
      const markdown = data.files["heure-de-la-semaine.md"].content

      this.planning = parserPlanningHebdomadaire(markdown)
      this.fallbackHtml = DOMPurify.sanitize(mdParser.render(markdown))
      this.texteComplementaireHtml = this.planning?.texteComplementaire ? DOMPurify.sanitize(mdParser.render(this.planning.texteComplementaire)) : ""
    } catch (error) {
      console.error("Erreur lors de la récupération du planning hebdomadaire :", error)
      this.planning = null
      this.fallbackHtml = ""
    }
  }

  render() {
    return html`
      <h1>Echanges pokémons</h1>
      <p class="intro">Notez les pokémons que vous avez et ceux que vous cherchez, et échangez-les avec votre communauté.</p>
      <hr class="separateur" />

      <p-planning-hebdomadaire
        .semaine=${this.planning?.semaine ?? ""}
        .jours=${this.planning?.jours ?? []}
        .texteComplementaireHtml=${this.texteComplementaireHtml}
        .fallbackHtml=${this.fallbackHtml}
      ></p-planning-hebdomadaire>

      <nav class="en-savoir-plus">
        <p class="titre">En savoir plus</p>
        <div class="liste-liens">
          ${LIENS_EN_SAVOIR_PLUS.map((lien) => html`<a href=${lien.href}>${lien.libelle}</a>`)}
        </div>
      </nav>
    `
  }
}
customElements.define("p-page-accueil", PageAccueil)
